import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Scale, Sparkles } from "lucide-react";
import { Link } from "react-router";
import Images from "../../utils/Images";

const partners = [
  { name: "Enterprise Group", logo: Images.enterprise },
  { name: "GCB Bank", logo: Images.gcb },
  { name: "GLICO Group", logo: Images.glico },
  { name: "Ecobank Ghana", logo: Images.ecobank },
  { name: "Hollard Ghana", logo: Images.hollard },
  { name: "SIC Insurance", logo: Images.sic },
  { name: "Stanbic Bank", logo: Images.stanbic },
  { name: "Zenith Bank", logo: Images.zenith },
];

const topRow = [...partners, ...partners];
const bottomRow = [...partners.slice().reverse(), ...partners.slice().reverse()];

const Partners = () => (
  <section className="relative w-full overflow-x-hidden bg-[#f7f4ec] py-16 sm:py-20 lg:py-24">
    <div
      className="pointer-events-none absolute left-1/2 top-0 h-72 w-[min(100%,72rem)] -translate-x-1/2 bg-gradient-to-b from-[#FFD221]/18 via-transparent to-transparent blur-3xl"
      aria-hidden
    />
    <div
      className="pointer-events-none absolute -right-24 bottom-32 h-72 w-72 rounded-full bg-[#FFD221]/08 blur-3xl"
      aria-hidden
    />

    {/* Intro — contained */}
    <div className="relative z-10 px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
      <motion.div
        className="mx-auto mb-10 max-w-2xl text-center lg:mb-12"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/20 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00] shadow-sm">
          <Sparkles size={14} strokeWidth={2.5} className="text-[#c9a000]" aria-hidden />
          Trusted partners
        </span>
        <h2 className="mt-3 text-[clamp(1.85rem,3.5vw+0.65rem,2.85rem)] font-black leading-tight tracking-tight text-gray-950">
          Our Trusted Partners
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
          Each category on InfoBoxx is powered by verified partners who provide real services — not just
          information.
        </p>
      </motion.div>
    </div>

    {/* Full-viewport-width featured strip — Afrimore */}
    <div
      className="relative z-10 mb-12 w-screen max-w-[100vw] lg:mb-16"
      style={{ marginLeft: "calc(50% - 50vw)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
      <div className="overflow-hidden relative px-6 py-12 bg-gray-950 sm:px-10 sm:py-14 lg:px-16 lg:py-16">
        {/* gold accents */}
        <div
          className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#FFD221] to-transparent opacity-90"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-20 top-1/2 h-[min(120%,480px)] w-[min(90vw,520px)] -translate-y-1/2 rounded-full bg-[#FFD221]/12 blur-[100px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-32 bottom-0 h-64 w-64 rounded-full bg-[#FFD221]/08 blur-[80px]"
          aria-hidden
        />

        <div className="relative mx-auto grid max-w-[90rem] gap-10 lg:grid-cols-12 lg:gap-12 lg:items-center">
          <div className="lg:col-span-7">
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/45 bg-[#FFD221]/15 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
              <Scale size={13} strokeWidth={2.5} className="text-[#FFD221]" aria-hidden />
              Featured partner
            </span>
            <p className="text-[clamp(2.75rem,7vw+0.5rem,4.5rem)] font-black leading-[0.95] tracking-tight text-white [text-shadow:0_2px_40px_rgba(255,210,33,0.15)]">
              Afrimore
            </p>
            <p className="mt-4 text-xs font-black uppercase tracking-[0.35em] text-[#FFD221] sm:text-sm">
              Legal
            </p>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
              Primary legal partner powering trusted guidance and verified professional services on the platform.
            </p>
            <ul className="grid gap-3 mt-8 sm:grid-cols-2 sm:gap-x-8">
              {["Verified partner", "Real services — not just info", "Ghana-registered professionals"].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-[13px] font-semibold text-white/80">
                  <CheckCircle2 size={16} strokeWidth={2.5} className="flex-shrink-0 text-[#FFD221]" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link
                to="/legal"
                className="inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-7 py-3.5 text-sm font-black text-black shadow-[0_12px_40px_-12px_rgba(255,210,33,0.55)] transition hover:bg-[#ffdf44]"
              >
                View legal
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
            </div>
          </div>

          <div className="relative lg:col-span-5">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#FFD221]/50 via-[#ffd221]/20 to-transparent opacity-80 blur-md" aria-hidden />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/12 bg-white p-8 shadow-[0_32px_80px_-32px_rgba(0,0,0,0.65)] sm:p-10 lg:p-12">
              <div className="absolute right-4 top-4 h-20 w-20 rounded-full bg-[#FFD221]/15 blur-2xl" aria-hidden />
              <img
                src={Images.afrimore}
                alt=""
                aria-hidden
                className="relative mx-auto h-auto w-full max-w-[17rem] object-contain sm:max-w-[20rem]"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
      </motion.div>
    </div>

    {/* Horizontal marquees — edge-to-edge below featured */}
    <div className="relative z-10">
     

      <div
        className="relative w-screen max-w-[100vw]"
        style={{ marginLeft: "calc(50% - 50vw)" }}
      >
        <div className="relative py-2 space-y-4">
          <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-12 bg-gradient-to-r from-[#f7f4ec] to-transparent sm:w-20 lg:w-28" />
          <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-12 bg-gradient-to-l from-[#f7f4ec] to-transparent sm:w-20 lg:w-28" />

          <motion.div
            className="flex gap-4 items-center"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
            style={{ width: "max-content" }}
          >
            {topRow.map((p, i) => (
              <div
                key={`${p.name}-t-${i}`}
                className="flex h-16 flex-shrink-0 items-center justify-center rounded-2xl border border-black/10 bg-white px-6 shadow-[0_12px_36px_-24px_rgba(0,0,0,0.35)] ring-1 ring-black/[0.04] transition hover:border-[#FFD221]/50 hover:shadow-[0_16px_40px_-20px_rgba(255,210,33,0.25)]"
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className="h-8 max-w-[7.5rem] object-contain opacity-95"
                  loading="lazy"
                />
              </div>
            ))}
          </motion.div>

          <motion.div
            className="flex gap-4 items-center"
            animate={{ x: ["-50%", "0%"] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ width: "max-content" }}
          >
            {bottomRow.map((p, i) => (
              <div
                key={`${p.name}-b-${i}`}
                className="flex h-16 flex-shrink-0 items-center justify-center rounded-2xl border border-black/10 bg-white px-6 shadow-[0_12px_36px_-24px_rgba(0,0,0,0.3)] ring-1 ring-black/[0.04] transition hover:border-[#FFD221]/50 hover:shadow-[0_16px_40px_-20px_rgba(255,210,33,0.25)]"
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className="h-8 max-w-[7.5rem] object-contain opacity-92"
                  loading="lazy"
                />
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

export default Partners;
